<template>
  <div class="excel-pivot-page" :style="previewThemeVars">
    <el-card shadow="hover" class="panel-card">
      <template #header>
        <div class="panel-header">
          <div class="title-wrap">
            <span>Excel 行列提取转换</span>
            <span ref="guideBtnRef" class="guide-btn-anchor">
              <el-button link type="primary" @click="openUsageGuide">使用说明</el-button>
            </span>
          </div>
          <span class="panel-tip">支持粘贴表格 / 上传 csv、xlsx，快速转成可复制到 Excel 的结构</span>
        </div>
      </template>

      <div ref="uploadAreaRef" class="input-row">
        <el-upload
          :auto-upload="false"
          :show-file-list="false"
          accept=".csv,.xlsx,.xls,.txt"
          :on-change="handleFileChange"
        >
          <el-button type="primary">上传文件（csv/xlsx）</el-button>
        </el-upload>
        <el-button plain @click="loadSampleData">加载示例</el-button>
        <el-button plain type="danger" @click="clearAll">清空</el-button>
      </div>

      <div ref="pasteAreaRef">
        <el-input
          v-model="pasteText"
          type="textarea"
          :rows="8"
          placeholder="可直接粘贴 Excel 内容（含表头）..."
        />
      </div>
      <div class="input-row">
        <el-button type="success" @click="parsePastedText">解析粘贴内容</el-button>
        <span class="panel-tip">当前原始数据：{{ rawRows.length }} 行，{{ columns.length }} 列</span>
      </div>
    </el-card>

    <el-card shadow="hover" class="panel-card" v-loading="transforming">
      <template #header>
        <div class="panel-header">
          <span>转换配置</span>
          <span class="panel-tip">默认以日期列作表头，其余列按维度/指标组合输出</span>
        </div>
      </template>

      <div ref="configAreaRef" class="config-grid">
        <div class="config-item">
          <span class="label">表头列（默认日期）</span>
          <el-select v-model="config.headerColumn" filterable>
            <el-option v-for="col in columns" :key="col" :label="col" :value="col" />
          </el-select>
        </div>
        <div class="config-item">
          <span class="label">行维度列</span>
          <el-select v-model="config.rowGroupColumn" filterable>
            <el-option v-for="col in availableDimensionColumns" :key="col" :label="col" :value="col" />
          </el-select>
        </div>
        <div class="config-item">
          <span class="label">指标列（作为二级表头）</span>
          <el-select v-model="config.metricColumns" multiple filterable collapse-tags collapse-tags-tooltip>
            <el-option v-for="col in availableMetricColumns" :key="col" :label="col" :value="col" />
          </el-select>
        </div>
        <template v-if="config.metricColumns.length">
          <div class="config-item" v-for="metric in config.metricColumns" :key="`alias_${metric}`">
            <span class="label">二级表头名称（{{ metric }}）</span>
            <el-input
              :model-value="metricDisplayName(metric)"
              placeholder="默认使用原字段名"
              @update:model-value="(value) => setMetricAlias(metric, value)"
            />
          </div>
        </template>
        <div class="config-item" v-if="!isSecondaryHeaderConfigMode">
          <span class="label">第一列显示名称</span>
          <el-input v-model="config.firstColumnLabel" placeholder="如：indexname / 维度" />
        </div>
        <template v-if="isSecondaryHeaderConfigMode">
          <div class="config-item">
            <span class="label">二级表头第一列（上）</span>
            <el-input v-model="config.secondHeaderTopLabel" placeholder="默认：第一列显示名称" />
          </div>
          <div class="config-item">
            <span class="label">二级表头第一列（下）</span>
            <el-input v-model="config.secondHeaderBottomLabel" placeholder="默认：行维度列名" />
          </div>
        </template>
      </div>

      <div class="input-row">
        <el-switch v-model="config.enableSecondaryHeader" active-text="启用二级表头" />
        <el-switch v-model="config.sortHeaderAsc" active-text="表头值升序" />
        <el-switch v-model="config.sortFirstColumnAsc" active-text="第一列值升序" />
        <el-switch v-model="config.fillMissingDateHeaders" active-text="日期缺失自动补0（按天）" />
      </div>
    </el-card>

    <div ref="previewAreaRef">
      <el-card shadow="hover" class="panel-card" v-loading="transforming">
        <template #header>
          <div class="panel-header">
            <span>结果预览</span>
            <div ref="actionsAreaRef" class="preview-actions">
              <el-button :disabled="!columns.length" @click="swapRowAndMetricColumns">行列转换</el-button>
              <el-button type="primary" :disabled="!resultAoa.length" @click="copyResultToClipboard">复制结果（TSV）</el-button>
              <el-button type="success" :disabled="!resultAoa.length" @click="exportResultExcel">导出 Excel</el-button>
              <el-button type="warning" :disabled="!rawRows.length" @click="openTemplateExportDialog">按模板导出</el-button>
              <el-select v-model="exportThemeId" size="small" class="theme-select" placeholder="导出主题">
                <el-option v-for="theme in exportThemeOptions" :key="theme.id" :label="theme.name" :value="theme.id" />
              </el-select>
            </div>
          </div>
        </template>

        <div class="panel-tip">
          结果总行数：{{ Math.max(resultAoa.length - 1, 0) }}，总列数：{{ resultAoa[0]?.length || 0 }}；
          为了性能，表格仅预览前 {{ previewRowLimit }} 行。
        </div>

        <el-empty v-if="!previewRows.length && !transforming" description="暂无可预览数据" />
        <div v-else class="table-scroll-wrap">
          <div class="preview-table-box">
            <table class="preview-matrix">
              <thead>
                <tr v-if="isSecondaryHeaderMode">
                  <th class="first-col">{{ secondHeaderTopLabelUsed }}</th>
                  <th v-for="header in headerValuesUsed" :key="`h1_${header}`" :colspan="metricColumnsUsed.length">
                    {{ header }}
                  </th>
                </tr>
                <tr v-if="isSecondaryHeaderMode">
                  <th class="first-col">{{ secondHeaderBottomLabelUsed }}</th>
                  <template v-for="header in headerValuesUsed" :key="`h2_${header}`">
                    <th v-for="metric in metricColumnsUsed" :key="`h2_${header}_${metric}`">
                      {{ metricDisplayName(metric) }}
                    </th>
                  </template>
                </tr>
                <tr v-else>
                  <th class="first-col">{{ config.firstColumnLabel || '维度' }}</th>
                  <th v-for="header in headerValuesUsed" :key="`h0_${header}`">{{ header }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in previewRows" :key="row.__rowTitle">
                  <td class="first-col">{{ row.__rowTitle }}</td>
                  <template v-if="isSecondaryHeaderMode">
                    <template v-for="header in headerValuesUsed" :key="`b1_${row.__rowTitle}_${header}`">
                      <td v-for="metric in metricColumnsUsed" :key="`b1_${row.__rowTitle}_${header}_${metric}`">
                        {{ row[cellProp(header, metric)] ?? 0 }}
                      </td>
                    </template>
                  </template>
                  <template v-else>
                    <td v-for="header in headerValuesUsed" :key="`b0_${row.__rowTitle}_${header}`">
                      {{ row[cellProp(header, primaryMetricColumn)] ?? 0 }}
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </el-card>
    </div>

    <el-tour
      v-model="usageGuideVisible"
      v-model:current="usageGuideStep"
      :close-on-press-escape="true"
      :show-arrow="true"
      :mask="true"
      @close="resetUsageGuide"
    >
      <el-tour-step
        :target="guideBtnRef"
        title="打开使用引导"
        description="点击这里可随时重新查看步骤引导。按 Esc 或右上角关闭可中途退出。"
      />
      <el-tour-step
        :target="uploadAreaRef"
        title="导入数据"
        description="支持上传 csv/xlsx/xls，或加载示例。"
      />
      <el-tour-step
        :target="pasteAreaRef"
        title="粘贴数据"
        description="可直接粘贴 Excel 表格内容（带表头），点击“解析粘贴内容”。"
      />
      <el-tour-step
        :target="configAreaRef"
        title="设置转换规则"
        description="选择表头列（常用日期）、行维度列、指标列，支持二级表头。"
      />
      <el-tour-step
        :target="previewAreaRef"
        title="预览结果"
        description="这里会展示转换后的结构化结果，便于核对。"
      />
      <el-tour-step
        :target="actionsAreaRef"
        title="复制与导出"
        description="可一键复制 TSV 到 Excel，或导出为 Excel 文件。"
      />
    </el-tour>

    <el-dialog v-model="templateExport.visible" title="按模板导出 Excel" width="840px">
      <div class="config-grid">
        <div class="config-item">
          <span class="label">模板文件（xlsx/xls）</span>
          <el-upload
            :auto-upload="false"
            :show-file-list="false"
            accept=".xlsx,.xls"
            :on-change="handleTemplateFileChange"
          >
            <el-button type="primary">上传模板</el-button>
          </el-upload>
          <span class="panel-tip">{{ templateExport.fileName || '未选择模板文件' }}</span>
        </div>
        <div class="config-item">
          <span class="label">模板工作表</span>
          <el-select v-model="templateExport.sheetName" :disabled="!templateExport.sheetNames.length" filterable>
            <el-option v-for="name in templateExport.sheetNames" :key="name" :label="name" :value="name" />
          </el-select>
        </div>
        <div class="config-item">
          <span class="label">日期方向</span>
          <el-select v-model="templateExport.dateAxis">
            <el-option label="日期在第一列（按匹配行）" value="column" />
            <el-option label="日期在第一行（按匹配列）" value="row" />
          </el-select>
        </div>
        <div class="config-item">
          <span class="label">表头开始行（默认 1）</span>
          <el-input-number v-model="templateExport.headerStartRow" :min="1" :step="1" controls-position="right" />
        </div>
        <div class="config-item">
          <span class="label">数据开始行</span>
          <el-input-number v-model="templateExport.dataStartRow" :min="1" :step="1" controls-position="right" />
        </div>
        <div class="config-item">
          <span class="label">数据开始列（A=1）</span>
          <el-input-number v-model="templateExport.dataStartCol" :min="1" :step="1" controls-position="right" />
        </div>
        <div class="config-item" v-if="templateExport.dateAxis === 'column'">
          <span class="label">日期列（A=1）</span>
          <el-input-number v-model="templateExport.dateCol" :min="1" :step="1" controls-position="right" />
        </div>
        <div class="config-item" v-if="templateExport.dateAxis === 'column'">
          <span class="label">自定义匹配指标所在行（默认表头开始+2）</span>
          <el-input-number v-model="templateExport.matchRow" :min="1" :step="1" controls-position="right" />
        </div>
        <div class="config-item" v-if="templateExport.dateAxis === 'row'">
          <span class="label">日期行</span>
          <el-input-number v-model="templateExport.dateRow" :min="1" :step="1" controls-position="right" />
        </div>
        <div class="config-item" v-if="templateExport.dateAxis === 'row'">
          <span class="label">匹配列（A=1）</span>
          <el-input-number v-model="templateExport.matchCol" :min="1" :step="1" controls-position="right" />
        </div>
        <div class="config-item">
          <span class="label">二级表头指标识别行（可选）</span>
          <el-input-number v-model="templateExport.metricHintRow" :min="1" :step="1" controls-position="right" />
        </div>
        <div class="config-item">
          <span class="label">源数据日期列</span>
          <el-select v-model="templateExport.sourceDateColumn" filterable>
            <el-option v-for="col in sourceDateColumnOptions" :key="`src_date_${col}`" :label="col" :value="col" />
          </el-select>
        </div>
        <div class="config-item">
          <span class="label">预览最大行数（最多显示）</span>
          <el-input-number v-model="templateExport.previewMaxRows" :min="10" :max="300" :step="1" controls-position="right" />
        </div>
        <div class="config-item">
          <span class="label">预览最大列数（最多显示）</span>
          <el-input-number v-model="templateExport.previewMaxCols" :min="8" :max="80" :step="1" controls-position="right" />
        </div>
        <div class="config-item">
          <span class="label">自定义指标匹配原维度</span>
          <el-select v-model="templateExport.matchSourceColumn" filterable>
            <el-option v-for="col in matchSourceColumnOptions" :key="`match_src_${col}`" :label="col" :value="col" />
          </el-select>
        </div>
        <div class="config-item">
          <el-switch v-model="templateExport.autoExtendTemplateDateRange" active-text="按源数据自动扩展模板日期范围" />
        </div>
        <div class="config-item template-metric-map">
          <span class="label">指标映射（模板指标 -> 源数据列）</span>
          <el-empty v-if="!templateMetricOptions.length" description="暂无可识别模板指标，调整匹配行/指标识别行后会自动读取" />
          <div v-else class="metric-binding-list">
            <div class="metric-binding-row" v-for="metricText in templateMetricOptions" :key="`bind_${metricText}`">
              <span class="metric-binding-left">{{ metricText }}</span>
              <el-select
                :model-value="templateExport.metricBindingMap[metricText] || ''"
                filterable
                clearable
                placeholder="选择源数据列"
                @update:model-value="(value) => setTemplateMetricBinding(metricText, value)"
              >
                <el-option v-for="col in metricColumnOptions" :key="`metric_col_${col}`" :label="col" :value="col" />
              </el-select>
            </div>
          </div>
          <span class="panel-tip">未匹配指标可在这里直接下拉选择，预览会实时刷新。</span>
        </div>
      </div>

      <div class="template-preview">
        <div class="panel-header">
          <span>导出结果预览（实时）</span>
          <span class="panel-tip">
            预览区域：第 {{ templatePreviewWindow.startRow }}-{{ templatePreviewWindow.endRow }} 行，
            {{ templatePreviewWindow.startColLabel }}-{{ templatePreviewWindow.endColLabel }} 列；
            扫描 {{ templatePreviewStats.scannedCells }} 格，候选 {{ templatePreviewStats.candidateCells }}，命中来源 {{ templatePreviewStats.matchedCells }}
          </span>
          <el-select v-model="exportThemeId" size="small" class="theme-select" placeholder="导出主题">
            <el-option v-for="theme in exportThemeOptions" :key="`dialog_${theme.id}`" :label="theme.name" :value="theme.id" />
          </el-select>
        </div>
        <el-empty v-if="!templatePreviewGrid.length" description="暂无可预览内容（请先上传模板并调整配置）" />
        <div v-else class="table-scroll-wrap">
          <table class="preview-matrix template-preview-matrix">
            <thead>
              <tr>
                <th>#</th>
                <th v-for="colHeader in templatePreviewColHeaders" :key="`col_${colHeader}`">{{ colHeader }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in templatePreviewGrid" :key="`r_${row.rowNumber}`">
                <td class="template-row-header">{{ row.rowNumber }}</td>
                <td
                  v-for="cell in row.cells"
                  :key="cell.address"
                  :class="{
                    'template-filled-cell': cell.fillStatus === 'matched',
                    'template-missing-cell': cell.fillStatus === 'missing'
                  }"
                  :title="cell.address"
                >
                  {{ cell.value }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="panel-tip">
          指标未匹配：{{ templatePreviewStats.unresolvedMetricCells }}，源数据缺失（补0）：{{ templatePreviewStats.missingSourceCells }}。
          绿色=命中来源数据，浅黄=未命中来源后补0。
        </div>
        <div class="panel-tip template-debug-tip">
          调试：轴={{ templateDebug.axis }}；源日期列={{ templateDebug.sourceDateColumn }}；
          模板 {{ templateDebug.templateMin || '-' }} ~ {{ templateDebug.templateMax || '-' }}；
          源数据 {{ templateDebug.sourceMin || '-' }} ~ {{ templateDebug.sourceMax || '-' }}；
          需扩展 {{ templateDebug.needDays }} 天；
          末端 {{ templateDebug.beforeEnd || '-' }} → {{ templateDebug.afterEnd || '-' }}；
          原因={{ templateDebug.reason || '-' }}
        </div>
      </div>

      <template #footer>
        <el-button @click="templateExport.visible = false">取消</el-button>
        <el-button type="warning" :disabled="!templateExport.workbook" @click="exportByTemplate">按模板导出</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import * as XLSX from 'xlsx'

const exportThemeOptions = [
  { id: 'business_blue', name: '商务蓝灰', border: '#8FA3BF', text: '#1F2D3D', headerBg: '#DCEBFF', firstColBg: '#E8F1FB' },
  { id: 'teal_green', name: '清爽青绿', border: '#89B8AA', text: '#1E3A34', headerBg: '#DDF4EE', firstColBg: '#ECF9F5' },
  { id: 'warm_orange', name: '暖橙奶油', border: '#C8A27A', text: '#4A3422', headerBg: '#FFE7CC', firstColBg: '#FFF2E3' },
  { id: 'neutral_gray', name: '中性灰', border: '#9AA3AF', text: '#1F2937', headerBg: '#EAECEF', firstColBg: '#F3F4F6' }
]

const rawRows = ref([])
const columns = ref([])
const pasteText = ref('')
const transforming = ref(false)
const previewRowLimit = 300
const exportThemeId = ref('business_blue')
let transformTimer = null
const usageGuideVisible = ref(false)
const usageGuideStep = ref(0)
const guideBtnRef = ref(null)
const uploadAreaRef = ref(null)
const pasteAreaRef = ref(null)
const configAreaRef = ref(null)
const previewAreaRef = ref(null)
const actionsAreaRef = ref(null)
let lockedScrollTop = 0

function setTourScrollLock(lock) {
  const body = document.body
  if (lock) {
    if (body.style.position === 'fixed') return
    lockedScrollTop = window.scrollY || window.pageYOffset || 0
    body.style.position = 'fixed'
    body.style.top = `-${lockedScrollTop}px`
    body.style.left = '0'
    body.style.right = '0'
    body.style.width = '100%'
    body.style.overflowY = 'scroll'
    return
  }
  if (body.style.position !== 'fixed') return
  body.style.position = ''
  body.style.top = ''
  body.style.left = ''
  body.style.right = ''
  body.style.width = ''
  body.style.overflowY = ''
  window.scrollTo(0, lockedScrollTop)
}

const config = reactive({
  headerColumn: '',
  rowGroupColumn: '',
  metricColumns: [],
  metricAliasMap: {},
  firstColumnLabel: '维度',
  secondHeaderTopLabel: '',
  secondHeaderBottomLabel: '',
  enableSecondaryHeader: true,
  sortHeaderAsc: true,
  sortFirstColumnAsc: true,
  fillMissingDateHeaders: true
})

const templateExport = reactive({
  visible: false,
  workbook: null,
  fileName: '',
  sheetNames: [],
  sheetName: '',
  dateAxis: 'column',
  headerStartRow: 1,
  dataStartRow: 4,
  dataStartCol: 2,
  dateRow: 1,
  dateCol: 1,
  matchRow: 3,
  matchCol: 1,
  metricHintRow: 2,
  sourceDateColumn: '',
  previewMaxRows: 32,
  previewMaxCols: 20,
  autoExtendTemplateDateRange: true,
  matchSourceColumn: '',
  metricBindingMap: {}
})

const templateMetricOptions = ref([])
const templatePreviewGrid = ref([])
const templatePreviewColHeaders = ref([])
const templatePreviewWindow = reactive({
  startRow: 1,
  endRow: 1,
  startColLabel: 'A',
  endColLabel: 'A'
})
const templatePreviewStats = reactive({
  scannedCells: 0,
  candidateCells: 0,
  matchedCells: 0,
  unresolvedMetricCells: 0,
  missingSourceCells: 0
})
const templateDebug = reactive({
  axis: '',
  sourceDateColumn: '',
  templateMin: '',
  templateMax: '',
  sourceMin: '',
  sourceMax: '',
  needDays: 0,
  beforeEnd: '',
  afterEnd: '',
  reason: ''
})

const headerValuesUsed = ref([])
const metricColumnsUsed = ref([])
const previewRows = ref([])
const resultAoa = ref([])

const availableDimensionColumns = computed(() => {
  return columns.value.filter((col) => col !== config.headerColumn)
})

const availableMetricColumns = computed(() => {
  return columns.value.filter((col) => col !== config.headerColumn)
})

const sourceDateColumnOptions = computed(() => {
  return [...columns.value]
})

const matchSourceColumnOptions = computed(() => {
  return columns.value.filter((col) => col !== templateExport.sourceDateColumn)
})

const metricColumnOptions = computed(() => {
  return columns.value.filter((col) => col !== templateExport.sourceDateColumn && col !== templateExport.matchSourceColumn)
})

const isSecondaryHeaderConfigMode = computed(() => {
  return Boolean(config.enableSecondaryHeader && config.metricColumns.length > 1)
})

const isSecondaryHeaderMode = computed(() => {
  return Boolean(config.enableSecondaryHeader && metricColumnsUsed.value.length > 1)
})

const primaryMetricColumn = computed(() => {
  return metricColumnsUsed.value[0] || config.metricColumns[0] || ''
})

const secondHeaderTopLabelUsed = computed(() => {
  return config.secondHeaderTopLabel || config.firstColumnLabel || '维度'
})

const secondHeaderBottomLabelUsed = computed(() => {
  return config.secondHeaderBottomLabel || config.rowGroupColumn || '维度'
})

const metricAliasSignature = computed(() => {
  return config.metricColumns.map((metric) => `${metric}:${config.metricAliasMap[metric] ?? ''}`).join('|')
})

const metricBindingSignature = computed(() => {
  return Object.entries(templateExport.metricBindingMap)
    .filter(([key, value]) => String(key || '').trim() && String(value || '').trim())
    .sort((a, b) => a[0].localeCompare(b[0], 'zh-CN'))
    .map(([key, value]) => `${key}=>${value}`)
    .join('|')
})

const activeExportTheme = computed(() => {
  return exportThemeOptions.find((theme) => theme.id === exportThemeId.value) || exportThemeOptions[0]
})

const previewThemeVars = computed(() => {
  const theme = activeExportTheme.value
  return {
    '--preview-border-color': theme.border,
    '--preview-text-color': theme.text,
    '--preview-header-bg': theme.headerBg,
    '--preview-first-col-bg': theme.firstColBg,
    '--preview-first-col-body-bg': theme.firstColBg
  }
})

function openUsageGuide() {
  usageGuideVisible.value = true
  usageGuideStep.value = 0
}

function resetUsageGuide() {
  usageGuideStep.value = 0
  setTourScrollLock(false)
}

function toNumber(value) {
  if (value === null || value === undefined || value === '') return 0
  const cleaned = String(value).replace(/,/g, '')
  const num = Number(cleaned)
  return Number.isFinite(num) ? num : 0
}

function isNumericLike(value) {
  if (value === null || value === undefined || value === '') return false
  const text = String(value).trim().replace(/,/g, '')
  if (!text) return false
  return /^-?\d+(\.\d+)?$/.test(text)
}

function getColumnNumericRatio(rows, col, sampleLimit = 200) {
  if (!rows?.length) return 0
  const limit = Math.min(rows.length, sampleLimit)
  let nonEmpty = 0
  let numeric = 0
  for (let i = 0; i < limit; i += 1) {
    const value = rows[i]?.[col]
    if (value === null || value === undefined || String(value).trim() === '') continue
    nonEmpty += 1
    if (isNumericLike(value)) numeric += 1
  }
  if (!nonEmpty) return 0
  return numeric / nonEmpty
}

function mergeMetricValue(prev, nextRaw) {
  const nextText = String(nextRaw ?? '').trim()
  if (!nextText) return prev

  const nextIsNum = isNumericLike(nextText)
  if (prev === undefined || prev === null || prev === '') {
    return nextIsNum ? toNumber(nextText) : nextText
  }

  const prevIsNum = typeof prev === 'number' || isNumericLike(prev)
  if (prevIsNum && nextIsNum) {
    return toNumber(prev) + toNumber(nextText)
  }

  const list = []
  const seen = new Set()
  ;[String(prev), nextText].forEach((item) => {
    const text = String(item || '').trim()
    if (!text) return
    if (!seen.has(text)) {
      seen.add(text)
      list.push(text)
    }
  })
  return list.join('、')
}

function normalizeDateText(value) {
  const text = String(value ?? '').trim()
  if (!text) return text

  const datePart = text.match(/^(\d{4}[/-]\d{1,2}[/-]\d{1,2})/)?.[1] || text

  // yyyy-mm-dd / yyyy/m/d
  let match = datePart.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/)
  if (match) {
    const y = Number(match[1])
    const m = Number(match[2])
    const d = Number(match[3])
    if (y >= 1900 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    }
  }

  // yyyy.m.d
  match = datePart.match(/^(\d{4})\.(\d{1,2})\.(\d{1,2})$/)
  if (match) {
    const y = Number(match[1])
    const m = Number(match[2])
    const d = Number(match[3])
    if (y >= 1900 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    }
  }

  // yyyy年m月d日
  match = datePart.match(/^(\d{4})年(\d{1,2})月(\d{1,2})日?$/)
  if (match) {
    const y = Number(match[1])
    const m = Number(match[2])
    const d = Number(match[3])
    if (y >= 1900 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    }
  }

  // m/d/yy or m/d/yyyy
  match = datePart.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/)
  if (match) {
    const m = Number(match[1])
    const d = Number(match[2])
    let y = Number(match[3])
    if (y < 100) y += 2000
    if (y >= 1900 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    }
  }

  // m.d / m-d / m/d (no year) => default current year
  match = datePart.match(/^(\d{1,2})[./-](\d{1,2})$/)
  if (match) {
    const m = Number(match[1])
    const d = Number(match[2])
    const y = new Date().getFullYear()
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    }
  }

  return text
}

function extractMonthDay(value) {
  const text = String(value ?? '').trim()
  if (!text) return ''
  let match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (match) return `${match[2]}-${match[3]}`

  match = text.match(/^(\d{1,2})[./-](\d{1,2})(?:[./-](\d{2}|\d{4}))?$/)
  if (!match) return ''
  const m = Number(match[1])
  const d = Number(match[2])
  if (m < 1 || m > 12 || d < 1 || d > 31) return ''
  return `${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

function isDateLikeColumnName(columnName) {
  const col = String(columnName || '').toLowerCase()
  return ['time_scope', 'date', 'day', 'dt', '日期', '时间'].some((k) => col.includes(k))
}

function toDateObjectFromYmd(value) {
  const text = String(value ?? '').trim()
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return null
  const y = Number(match[1])
  const m = Number(match[2])
  const d = Number(match[3])
  const date = new Date(y, m - 1, d)
  if (Number.isNaN(date.getTime())) return null
  return date
}

function formatDateYmd(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function buildContinuousDateHeaders(values, asc = true) {
  if (!values.length) return []
  const normalized = values.map((v) => normalizeDateText(v)).filter((v) => String(v || '').trim() !== '')
  const validDateTexts = Array.from(new Set(normalized.filter((v) => toDateObjectFromYmd(v))))
  if (!validDateTexts.length) return Array.from(new Set(normalized))
  if (validDateTexts.length === 1) return validDateTexts

  const dates = validDateTexts.map((v) => toDateObjectFromYmd(v)).filter(Boolean)
  const sorted = [...dates].sort((a, b) => a.getTime() - b.getTime())
  const start = new Date(sorted[0])
  const end = new Date(sorted[sorted.length - 1])
  const list = []
  const cursor = new Date(start)
  while (cursor.getTime() <= end.getTime()) {
    list.push(formatDateYmd(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }
  return asc ? list : list.reverse()
}

function getDateParseRatio(values, sampleLimit = 200) {
  if (!values?.length) return 0
  const limit = Math.min(values.length, sampleLimit)
  let nonEmpty = 0
  let valid = 0
  for (let i = 0; i < limit; i += 1) {
    const text = String(values[i] ?? '').trim()
    if (!text) continue
    nonEmpty += 1
    const normalized = normalizeDateText(text)
    if (toDateObjectFromYmd(normalized)) valid += 1
  }
  if (!nonEmpty) return 0
  return valid / nonEmpty
}

function shouldFillDateHeaders(values, headerColumn) {
  if (isDateLikeColumnName(headerColumn)) return true
  return getDateParseRatio(values) >= 0.6
}

function normalizeDimensionValueByColumn(columnName, value) {
  if (!isDateLikeColumnName(columnName)) return String(value ?? '').trim() || '-'
  const normalized = normalizeDateText(value)
  return normalized || '-'
}

function normalizeHeader(header, index, used) {
  const base = String(header || '').trim() || `col_${index + 1}`
  if (!used[base]) {
    used[base] = 1
    return base
  }
  used[base] += 1
  return `${base}_${used[base]}`
}

function aoaToRows(aoa) {
  if (!Array.isArray(aoa) || aoa.length < 2) return { rows: [], headers: [] }
  const headerRaw = aoa[0] || []
  const used = {}
  const headers = headerRaw.map((h, idx) => normalizeHeader(h, idx, used))
  const rows = aoa
    .slice(1)
    .filter((row) => Array.isArray(row) && row.some((cell) => String(cell ?? '').trim() !== ''))
    .map((row) => {
      const obj = {}
      headers.forEach((h, idx) => {
        obj[h] = row[idx] ?? ''
      })
      return obj
    })
  return { rows, headers }
}

function detectDateLikeColumn(cols) {
  const found = cols.find((col) => isDateLikeColumnName(col))
  return found || cols[0] || ''
}

function guessDefaultMetrics(cols, headerCol, rows = []) {
  const keyWords = ['pv', 'uv', 'timeonsite', 'amount', 'cnt', 'count', 'num', 'rate', '订单', '人数', '金额', '商品数', '件数', '次数']
  return cols.filter((col) => {
    if (col === headerCol) return false
    const nameHit = keyWords.some((k) => col.toLowerCase().includes(String(k).toLowerCase()))
    const numericRatio = getColumnNumericRatio(rows, col)
    return nameHit || numericRatio >= 0.8
  })
}

function applyRawRows(rows, headers) {
  rawRows.value = rows
  columns.value = headers
  config.headerColumn = detectDateLikeColumn(headers)

  const defaultDims = headers.filter((c) => c !== config.headerColumn && !['pv', 'uv', 'timeonsite'].includes(c.toLowerCase()))
  config.rowGroupColumn = defaultDims.length ? defaultDims[0] : (headers.find((c) => c !== config.headerColumn) || '')

  const metrics = guessDefaultMetrics(headers, config.headerColumn, rows)
  const fallbackNumeric = headers
    .filter((c) => c !== config.headerColumn && c !== config.rowGroupColumn)
    .filter((c) => getColumnNumericRatio(rows, c) >= 0.8)
  config.metricColumns = metrics.length
    ? metrics
    : (fallbackNumeric.length ? fallbackNumeric.slice(0, 2) : headers.filter((c) => c !== config.headerColumn && c !== config.rowGroupColumn).slice(0, 2))
  config.metricAliasMap = {}
  config.metricColumns.forEach((metric) => {
    config.metricAliasMap[metric] = metric
  })
  config.firstColumnLabel = config.rowGroupColumn || '维度'
  config.secondHeaderTopLabel = config.firstColumnLabel
  config.secondHeaderBottomLabel = config.rowGroupColumn || '维度'
}

function parsePastedText() {
  const text = pasteText.value.trim()
  if (!text) {
    ElMessage.warning('请先粘贴内容')
    return
  }

  try {
    if (text.includes('\t')) {
      const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '')
      const aoa = lines.map((line) => line.split('\t'))
      const { rows, headers } = aoaToRows(aoa)
      applyRawRows(rows, headers)
      ElMessage.success(`解析成功：${rows.length} 行`)
      return
    }

    const workbook = XLSX.read(text, { type: 'string' })
    const sheet = workbook.Sheets[workbook.SheetNames[0]]
    const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' })
    const { rows, headers } = aoaToRows(aoa)
    applyRawRows(rows, headers)
    ElMessage.success(`解析成功：${rows.length} 行`)
  } catch (error) {
    console.error(error)
    ElMessage.error('解析粘贴内容失败，请确认格式')
  }
}

function scoreDecodedText(text) {
  if (!text) return -9999
  let score = 0
  const mojibakeMatches = text.match(/[ÃÂÐÑçæåäöüøðþ]/g) || []
  score -= mojibakeMatches.length * 2
  const replacementMatches = text.match(/�/g) || []
  score -= replacementMatches.length * 5
  const cjkMatches = text.match(/[\u4e00-\u9fff]/g) || []
  score += cjkMatches.length
  const lineBreaks = text.match(/\r?\n/g) || []
  score += lineBreaks.length
  return score
}

function decodeCsvBuffer(buffer) {
  // 1) 优先严格 UTF-8。若严格可解码，通常就是正确编码，避免被误判为 GBK 导致“鐞冩槦...”。
  try {
    const utf8Strict = new TextDecoder('utf-8', { fatal: true }).decode(buffer)
    if (utf8Strict) return utf8Strict
  } catch (error) {
    // UTF-8 严格失败才走中文编码兜底
    console.warn('utf-8 strict decode failed, fallback to gbk/gb18030', error)
  }

  // 2) UTF-8 严格失败后，再在 gbk / gb18030 中择优。
  const fallbackEncodings = ['gb18030', 'gbk', 'utf-8']
  let bestText = ''
  let bestScore = -Infinity
  fallbackEncodings.forEach((encoding) => {
    try {
      const text = new TextDecoder(encoding, { fatal: false }).decode(buffer)
      const score = scoreDecodedText(text)
      if (score > bestScore) {
        bestScore = score
        bestText = text
      }
    } catch (error) {
      console.warn(`decode with ${encoding} failed`, error)
    }
  })
  return bestText
}

function handleFileChange(fileInfo) {
  const rawFile = fileInfo?.raw
  if (!rawFile) return
  const isCsvLike = /\.(csv|txt)$/i.test(rawFile.name || '')
  const reader = new FileReader()
  reader.onload = (e) => {
    try {
      const data = e.target?.result
      let workbook
      if (isCsvLike) {
        const decoded = decodeCsvBuffer(data)
        workbook = XLSX.read(decoded, { type: 'string', raw: false })
      } else {
        workbook = XLSX.read(data, { type: 'array' })
      }
      const sheet = workbook.Sheets[workbook.SheetNames[0]]
      const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' })
      const { rows, headers } = aoaToRows(aoa)
      applyRawRows(rows, headers)
      ElMessage.success(`文件读取成功：${rows.length} 行`)
    } catch (error) {
      console.error(error)
      ElMessage.error('文件解析失败，请检查文件格式')
    }
  }
  reader.readAsArrayBuffer(rawFile)
}

function clearAll() {
  rawRows.value = []
  columns.value = []
  pasteText.value = ''
  previewRows.value = []
  resultAoa.value = []
  config.headerColumn = ''
  config.rowGroupColumn = ''
  config.metricColumns = []
  config.metricAliasMap = {}
  config.firstColumnLabel = '维度'
  config.secondHeaderTopLabel = ''
  config.secondHeaderBottomLabel = ''
  config.sortFirstColumnAsc = true
  config.fillMissingDateHeaders = true
}

function loadSampleData() {
  const aoa = [
    ['indexname', 'time_scope', 'pv', 'uv'],
    ['visit', '2026-03-03', '1024', '501'],
    ['visit', '2026-03-04', '1201', '630'],
    ['addShop', '2026-03-03', '380', '210'],
    ['addShop', '2026-03-04', '420', '268']
  ]
  const { rows, headers } = aoaToRows(aoa)
  applyRawRows(rows, headers)
  ElMessage.success('示例数据已加载')
}

function cellProp(header, metric) {
  return `${header}__${metric}`
}

function ensureMetricAliasDefaults() {
  config.metricColumns.forEach((metric) => {
    if (!(metric in config.metricAliasMap)) {
      config.metricAliasMap[metric] = metric
    }
  })
}

function metricDisplayName(metric) {
  const alias = config.metricAliasMap[metric]
  return String(alias ?? '').trim() || metric
}

function setMetricAlias(metric, value) {
  config.metricAliasMap[metric] = value
}

function swapRowAndMetricColumns() {
  if (!columns.value.length) return
  if (!config.headerColumn || !config.rowGroupColumn) {
    ElMessage.warning('请先选择表头列和行维度列后再转换')
    return
  }

  const oldHeaderColumn = config.headerColumn
  const oldRowGroupColumn = config.rowGroupColumn
  const nextHeaderColumn = oldRowGroupColumn
  const nextRowGroupColumn = oldHeaderColumn

  config.headerColumn = nextHeaderColumn
  config.rowGroupColumn = nextRowGroupColumn
  config.firstColumnLabel = config.rowGroupColumn || '维度'

  if (!config.secondHeaderTopLabel) {
    config.secondHeaderTopLabel = config.firstColumnLabel || '维度'
  }
  config.secondHeaderBottomLabel = config.rowGroupColumn || '维度'

  ElMessage.success('已交换表头列与行维度列（行维度第1列作为新表头）')
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function buildTransformResult() {
  if (!rawRows.value.length || !config.headerColumn || !config.rowGroupColumn || !config.metricColumns.length) {
    headerValuesUsed.value = []
    metricColumnsUsed.value = []
    previewRows.value = []
    resultAoa.value = []
    return
  }

  const headerSet = new Set()
  const rowMap = new Map()
  const metricNumericMap = {}
  config.metricColumns.forEach((metric) => {
    metricNumericMap[metric] = getColumnNumericRatio(rawRows.value, metric) >= 0.8
  })

  rawRows.value.forEach((row) => {
    const headerVal = normalizeDateText(row[config.headerColumn] ?? '')
    if (!headerVal) return
    headerSet.add(headerVal)

    const rowKey = normalizeDimensionValueByColumn(config.rowGroupColumn, row[config.rowGroupColumn])
    if (!rowMap.has(rowKey)) rowMap.set(rowKey, {})
    const bucket = rowMap.get(rowKey)
    config.metricColumns.forEach((metric) => {
      const key = cellProp(headerVal, metric)
      bucket[key] = mergeMetricValue(bucket[key], row[metric])
    })
  })

  let headers = Array.from(headerSet)
  if (config.fillMissingDateHeaders && shouldFillDateHeaders(headers, config.headerColumn)) {
    headers = buildContinuousDateHeaders(headers, config.sortHeaderAsc)
  } else if (config.sortHeaderAsc) {
    headers.sort((a, b) => a.localeCompare(b, 'zh-CN'))
  } else {
    headers.sort((a, b) => b.localeCompare(a, 'zh-CN'))
  }
  headerValuesUsed.value = headers
  metricColumnsUsed.value = [...config.metricColumns]

  const rawRowKeys = Array.from(rowMap.keys())
  let rowKeysForOutput = rawRowKeys
  if (config.fillMissingDateHeaders && shouldFillDateHeaders(rawRowKeys, config.rowGroupColumn)) {
    rowKeysForOutput = buildContinuousDateHeaders(rawRowKeys, config.sortFirstColumnAsc)
    rowKeysForOutput.forEach((rowKey) => {
      if (!rowMap.has(rowKey)) rowMap.set(rowKey, {})
    })
  } else if (config.sortFirstColumnAsc) {
    rowKeysForOutput = [...rawRowKeys].sort((a, b) => a.localeCompare(b, 'zh-CN', { numeric: true, sensitivity: 'base' }))
  }
  const rowEntries = rowKeysForOutput.map((rowKey) => [rowKey, rowMap.get(rowKey) || {}])

  const transformed = []
  const aoa = []
  ensureMetricAliasDefaults()
  if (isSecondaryHeaderMode.value) {
    const head1 = [config.secondHeaderTopLabel || config.firstColumnLabel || '维度']
    headers.forEach((h) => {
      config.metricColumns.forEach(() => head1.push(h))
    })
    const head2 = [config.secondHeaderBottomLabel || config.rowGroupColumn || '维度']
    headers.forEach(() => {
      config.metricColumns.forEach((m) => head2.push(metricDisplayName(m)))
    })
    aoa.push(head1, head2)
  } else {
    aoa.push([config.firstColumnLabel || '维度', ...headers])
  }

  for (const [rowKey, bucket] of rowEntries) {
    const rowObj = { __rowTitle: rowKey }
    const aoaRow = [rowKey]
    if (isSecondaryHeaderMode.value) {
      headers.forEach((h) => {
        config.metricColumns.forEach((m) => {
          const key = cellProp(h, m)
          const value = bucket[key] ?? (metricNumericMap[m] ? 0 : '')
          rowObj[key] = value
          aoaRow.push(value)
        })
      })
    } else {
      const metric = primaryMetricColumn.value
      headers.forEach((h) => {
        const key = cellProp(h, metric)
        const value = bucket[key] ?? (metricNumericMap[metric] ? 0 : '')
        rowObj[key] = value
        aoaRow.push(value)
      })
    }
    transformed.push(rowObj)
    aoa.push(aoaRow)
  }

  previewRows.value = transformed.slice(0, previewRowLimit)
  resultAoa.value = aoa
}

function scheduleTransform() {
  if (transformTimer) clearTimeout(transformTimer)
  transforming.value = true
  transformTimer = setTimeout(() => {
    buildTransformResult()
    transforming.value = false
  }, 80)
}

watch(
  () => [
    rawRows.value.length,
    config.headerColumn,
    config.rowGroupColumn,
    config.metricColumns.join('|'),
    metricAliasSignature.value,
    config.firstColumnLabel,
    config.secondHeaderTopLabel,
    config.secondHeaderBottomLabel,
    config.enableSecondaryHeader,
    config.sortHeaderAsc,
    config.sortFirstColumnAsc,
    config.fillMissingDateHeaders
  ],
  () => {
    scheduleTransform()
  }
)

watch(
  () => usageGuideVisible.value,
  (visible) => {
    setTourScrollLock(visible)
  }
)

watch(
  () => templateExport.headerStartRow,
  (row) => {
    const headerRow = Math.max(1, Number(row || 1))
    templateExport.dateRow = headerRow
    templateExport.metricHintRow = headerRow + 1
    if (templateExport.dateAxis === 'column') {
      templateExport.matchRow = headerRow + (config.metricColumns.length > 1 ? 2 : 1)
      templateExport.dataStartRow = templateExport.matchRow + 1
    } else {
      templateExport.dataStartRow = headerRow + 1
    }
  }
)

watch(
  () => templateExport.dateAxis,
  (axis) => {
    const headerRow = Math.max(1, Number(templateExport.headerStartRow || 1))
    if (axis === 'column') {
      templateExport.dateCol = 1
      templateExport.matchRow = headerRow + (config.metricColumns.length > 1 ? 2 : 1)
      templateExport.dataStartRow = templateExport.matchRow + 1
      templateExport.dataStartCol = 2
      return
    }
    templateExport.dateRow = headerRow
    templateExport.matchCol = 1
    templateExport.dataStartRow = headerRow + 1
    templateExport.dataStartCol = 2
  }
)

watch(
  () => templateExport.sourceDateColumn,
  (dateCol) => {
    if (!dateCol) return
    if (templateExport.matchSourceColumn === dateCol) {
      templateExport.matchSourceColumn = matchSourceColumnOptions.value[0] || ''
    }
  }
)

watch(
  () => templateExport.matchSourceColumn,
  (dimensionCol) => {
    if (!dimensionCol) return
    if (dimensionCol === templateExport.sourceDateColumn) {
      templateExport.matchSourceColumn = matchSourceColumnOptions.value[0] || ''
      return
    }
    Object.keys(templateExport.metricBindingMap).forEach((key) => {
      const mapped = templateExport.metricBindingMap[key]
      if (!metricColumnOptions.value.includes(mapped)) {
        delete templateExport.metricBindingMap[key]
      }
    })
  }
)

watch(
  () => [
    templateExport.visible,
    Boolean(templateExport.workbook),
    templateExport.sheetName,
    templateExport.dateAxis,
    templateExport.dataStartRow,
    templateExport.dataStartCol,
    templateExport.matchRow,
    templateExport.matchCol,
    templateExport.metricHintRow,
    templateExport.sourceDateColumn,
    templateExport.matchSourceColumn,
    templateExport.previewMaxRows,
    templateExport.previewMaxCols,
    templateExport.autoExtendTemplateDateRange
  ],
  () => {
    updateTemplateMetricOptions()
  },
  { immediate: true }
)

watch(
  () => [
    templateExport.visible,
    Boolean(templateExport.workbook),
    templateExport.sheetName,
    templateExport.dateAxis,
    templateExport.headerStartRow,
    templateExport.dataStartRow,
    templateExport.dataStartCol,
    templateExport.dateRow,
    templateExport.dateCol,
    templateExport.matchRow,
    templateExport.matchCol,
    templateExport.metricHintRow,
    templateExport.sourceDateColumn,
    templateExport.matchSourceColumn,
    templateExport.previewMaxRows,
    templateExport.previewMaxCols,
    templateExport.autoExtendTemplateDateRange,
    metricBindingSignature.value,
    rawRows.value.length,
    config.headerColumn,
    config.rowGroupColumn,
    config.metricColumns.join('|'),
    metricAliasSignature.value
  ],
  () => {
    buildTemplatePreview()
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  setTourScrollLock(false)
})

onMounted(() => {
  // 兜底恢复，避免极端情况下上次引导状态残留
  setTourScrollLock(false)
})

function openTemplateExportDialog() {
  const baseHeaderRow = 1
  templateExport.headerStartRow = baseHeaderRow
  templateExport.dateRow = baseHeaderRow
  templateExport.dateCol = 1
  templateExport.metricHintRow = baseHeaderRow + 1
  templateExport.matchRow = baseHeaderRow + (config.metricColumns.length > 1 ? 2 : 1)
  templateExport.dataStartRow = templateExport.matchRow + 1
  templateExport.dataStartCol = 2
  templateExport.matchCol = 1
  const autoDateColumn = detectDateLikeColumn(columns.value)
  templateExport.sourceDateColumn = autoDateColumn || config.headerColumn || sourceDateColumnOptions.value[0] || ''
  templateExport.matchSourceColumn = config.rowGroupColumn || matchSourceColumnOptions.value.find((col) => col !== templateExport.sourceDateColumn) || ''
  templateExport.visible = true
}

function handleTemplateFileChange(fileInfo) {
  const rawFile = fileInfo?.raw
  if (!rawFile) return
  const reader = new FileReader()
  reader.onload = (e) => {
    try {
      const data = e.target?.result
      const workbook = XLSX.read(data, { type: 'array', cellStyles: true, cellNF: true, cellDates: true })
      templateExport.workbook = workbook
      templateExport.fileName = rawFile.name || ''
      templateExport.sheetNames = workbook.SheetNames || []
      templateExport.sheetName = templateExport.sheetNames[0] || ''
      const autoDateColumn = detectDateLikeColumn(columns.value)
      if (!templateExport.sourceDateColumn || !sourceDateColumnOptions.value.includes(templateExport.sourceDateColumn)) {
        templateExport.sourceDateColumn = autoDateColumn || config.headerColumn || sourceDateColumnOptions.value[0] || ''
      }
      if (!templateExport.matchSourceColumn || !matchSourceColumnOptions.value.includes(templateExport.matchSourceColumn)) {
        templateExport.matchSourceColumn = config.rowGroupColumn || matchSourceColumnOptions.value[0] || ''
      }
      templateExport.metricBindingMap = {}
      templateMetricOptions.value = []
      ElMessage.success('模板读取成功')
    } catch (error) {
      console.error(error)
      ElMessage.error('模板读取失败，请检查文件格式')
    }
  }
  reader.readAsArrayBuffer(rawFile)
}

function setTemplateMetricBinding(templateMetric, sourceColumn) {
  const key = String(templateMetric || '').trim()
  if (!key) return
  if (!sourceColumn) {
    delete templateExport.metricBindingMap[key]
    return
  }
  templateExport.metricBindingMap[key] = sourceColumn
}

function normalizeCompareText(value) {
  return String(value ?? '').trim().toLowerCase().replace(/\s+/g, '')
}

function resolveMetricColumnByName(metricText, sourceColumns = []) {
  const metricRaw = String(metricText || '').trim()
  if (!metricRaw) return ''

  const fromBinding = templateExport.metricBindingMap[metricRaw]
  if (fromBinding && sourceColumns.includes(fromBinding)) return fromBinding

  const normalized = normalizeCompareText(metricRaw)
  const mappedByNormalized = Object.entries(templateExport.metricBindingMap).find(([key]) => normalizeCompareText(key) === normalized)?.[1]
  if (mappedByNormalized && sourceColumns.includes(mappedByNormalized)) return mappedByNormalized

  if (sourceColumns.includes(metricRaw)) return metricRaw

  const exactByAlias = sourceColumns.find((metric) => normalizeCompareText(metricDisplayName(metric)) === normalized)
  if (exactByAlias) return exactByAlias

  const exactByName = sourceColumns.find((metric) => normalizeCompareText(metric) === normalized)
  if (exactByName) return exactByName

  const fuzzyByAlias = sourceColumns.find((metric) => normalizeCompareText(metricDisplayName(metric)).includes(normalized))
  if (fuzzyByAlias) return fuzzyByAlias

  const fuzzyByName = sourceColumns.find((metric) => normalizeCompareText(metric).includes(normalized))
  if (fuzzyByName) return fuzzyByName
  return ''
}

function splitMatchToken(matchText) {
  const raw = String(matchText ?? '').trim()
  if (!raw) return { dimension: '', metricText: '' }
  const normalized = raw.replace(/[＋|｜／,，、]/g, '+')
  const parts = normalized.split('+').map((part) => String(part || '').trim()).filter(Boolean)
  if (parts.length < 2) return { dimension: raw, metricText: '' }
  return {
    dimension: parts.slice(0, -1).join('+'),
    metricText: parts[parts.length - 1]
  }
}

function buildDataValueKey(dateText, dimensionText, metricColumn) {
  return `${normalizeCompareText(dateText)}||${normalizeCompareText(dimensionText)}||${normalizeCompareText(metricColumn)}`
}

function buildSourceValueMap(
  dateColumn = templateExport.sourceDateColumn || config.headerColumn,
  dimensionColumn = templateExport.matchSourceColumn || config.rowGroupColumn,
  sourceMetricColumns = metricColumnOptions.value
) {
  const valueMap = new Map()
  if (!dateColumn || !dimensionColumn || !sourceMetricColumns.length) return valueMap

  rawRows.value.forEach((row) => {
    const rawDateText = String(row[dateColumn] ?? '').trim()
    const dateValue = normalizeDateText(rawDateText)
    if (!dateValue) return
    const monthDay = extractMonthDay(rawDateText) || extractMonthDay(dateValue)
    const dimensionValue = normalizeDimensionValueByColumn(dimensionColumn, row[dimensionColumn])
    if (!dimensionValue) return
    sourceMetricColumns.forEach((metric) => {
      const key = buildDataValueKey(dateValue, dimensionValue, metric)
      const merged = mergeMetricValue(valueMap.get(key), row[metric])
      valueMap.set(key, merged)
      if (monthDay) {
        const mdKey = buildDataValueKey(monthDay, dimensionValue, metric)
        const mergedMd = mergeMetricValue(valueMap.get(mdKey), row[metric])
        valueMap.set(mdKey, mergedMd)
      }
    })
  })
  return valueMap
}

function buildMergedCellMasterMap(sheet) {
  const map = new Map()
  const merges = sheet?.['!merges'] || []
  merges.forEach((mergeRange) => {
    const master = { r: mergeRange.s.r, c: mergeRange.s.c }
    for (let r = mergeRange.s.r; r <= mergeRange.e.r; r += 1) {
      for (let c = mergeRange.s.c; c <= mergeRange.e.c; c += 1) {
        map.set(`${r},${c}`, master)
      }
    }
  })
  return map
}

function getSheetCellText(sheet, row0, col0, mergedCellMasterMap = null) {
  if (row0 < 0 || col0 < 0) return ''
  let realRow = row0
  let realCol = col0
  if (mergedCellMasterMap) {
    const master = mergedCellMasterMap.get(`${row0},${col0}`)
    if (master) {
      realRow = master.r
      realCol = master.c
    }
  }
  const addr = XLSX.utils.encode_cell({ r: realRow, c: realCol })
  const cell = sheet[addr]
  if (!cell) return ''
  return String(cell.w ?? cell.v ?? '').trim()
}

function formatYmdFromParts(y, m, d) {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

function getSheetCellDateText(sheet, row0, col0, mergedCellMasterMap = null) {
  if (row0 < 0 || col0 < 0) return ''
  let realRow = row0
  let realCol = col0
  if (mergedCellMasterMap) {
    const master = mergedCellMasterMap.get(`${row0},${col0}`)
    if (master) {
      realRow = master.r
      realCol = master.c
    }
  }
  const addr = XLSX.utils.encode_cell({ r: realRow, c: realCol })
  const cell = sheet[addr]
  if (!cell) return ''

  // Prefer rendered text first to avoid timezone/date-serial off-by-one issues.
  const rendered = normalizeDateText(cell.w ?? '')
  if (toDateObjectFromYmd(rendered)) return rendered

  if (cell.t === 'd' && cell.v instanceof Date && !Number.isNaN(cell.v.getTime())) {
    return formatYmdFromParts(cell.v.getFullYear(), cell.v.getMonth() + 1, cell.v.getDate())
  }

  if (cell.t === 'n' && Number.isFinite(cell.v)) {
    const parsed = XLSX.SSF.parse_date_code(cell.v)
    if (parsed?.y && parsed?.m && parsed?.d) {
      return formatYmdFromParts(parsed.y, parsed.m, parsed.d)
    }
  }

  return getSheetCellText(sheet, row0, col0, mergedCellMasterMap)
}

function shiftYmd(ymd, offsetDays) {
  const date = toDateObjectFromYmd(ymd)
  if (!date) return ''
  const next = new Date(date)
  next.setDate(next.getDate() + offsetDays)
  return formatDateYmd(next)
}

function dayStartMs(ymd) {
  const date = toDateObjectFromYmd(ymd)
  if (!date) return NaN
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

function dayDiff(fromYmd, toYmd) {
  const fromMs = dayStartMs(fromYmd)
  const toMs = dayStartMs(toYmd)
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) return 0
  return Math.floor((toMs - fromMs) / 86400000)
}

function getSourceDateRange(columnName) {
  if (!columnName) return { min: '', max: '' }
  let min = ''
  let max = ''
  rawRows.value.forEach((row) => {
    const normalized = normalizeDateText(row?.[columnName] ?? '')
    if (!toDateObjectFromYmd(normalized)) return
    if (!min || dayDiff(normalized, min) > 0) min = normalized
    if (!max || dayDiff(max, normalized) > 0) max = normalized
  })
  return { min, max }
}

function copyTemplateCellStyle(sheet, fromRow, fromCol, toRow, toCol) {
  const fromAddr = XLSX.utils.encode_cell({ r: fromRow, c: fromCol })
  const toAddr = XLSX.utils.encode_cell({ r: toRow, c: toCol })
  const fromCell = sheet[fromAddr]
  const toCell = sheet[toAddr] || {}
  if (!fromCell) {
    sheet[toAddr] = toCell
    return
  }
  if (fromCell.s !== undefined) toCell.s = fromCell.s
  if (fromCell.z !== undefined) toCell.z = fromCell.z
  if (fromCell.t === 's' || fromCell.t === 'str') {
    toCell.t = fromCell.t
    toCell.v = fromCell.v
    if (fromCell.w !== undefined) toCell.w = fromCell.w
  } else if (fromCell.t === 'n' && Number.isFinite(fromCell.v)) {
    toCell.t = 'n'
    toCell.v = fromCell.v
    if (fromCell.w !== undefined) toCell.w = fromCell.w
  }
  sheet[toAddr] = toCell
}

function cloneSheetForPreview(sheet) {
  if (!sheet) return null
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(sheet)
    } catch (error) {
      // fallback below
    }
  }
  return JSON.parse(JSON.stringify(sheet))
}

function extendTemplateRangeIfNeeded(sheet, range, opts) {
  const {
    axis,
    startRow,
    startCol,
    dateRow,
    dateCol,
    sourceDateColumn,
    autoExtend
  } = opts
  templateDebug.axis = axis
  templateDebug.sourceDateColumn = sourceDateColumn || ''
  templateDebug.beforeEnd = axis === 'column' ? String(range.e.r + 1) : colIndexToLabel(range.e.c)
  templateDebug.afterEnd = templateDebug.beforeEnd
  templateDebug.needDays = 0
  templateDebug.reason = ''
  if (!autoExtend) {
    templateDebug.reason = 'autoExtend=false'
    return range
  }

  const mergedCellMasterMap = buildMergedCellMasterMap(sheet)
  const startIndex = axis === 'column' ? startRow : startCol
  const endIndex = axis === 'column' ? range.e.r : range.e.c
  const dateMap = buildTemplateDateIndexMap({
    sheet,
    mergedCellMasterMap,
    axis,
    startIndex,
    endIndex,
    dateRow,
    dateCol,
    enableContinuousFill: false
  })
  let templateDates = Array.from(dateMap.values()).filter((v) => toDateObjectFromYmd(v))
  if (!templateDates.length) {
    // Fallback: scan the whole current template axis to find the latest date,
    // avoiding failures when dataStartRow/Col points past existing data.
    const fallbackStart = axis === 'column' ? range.s.r : range.s.c
    const fallbackEnd = axis === 'column' ? range.e.r : range.e.c
    const fallback = []
    for (let idx = fallbackStart; idx <= fallbackEnd; idx += 1) {
      const text = axis === 'column'
        ? getSheetCellDateText(sheet, idx, dateCol, mergedCellMasterMap)
        : getSheetCellDateText(sheet, dateRow, idx, mergedCellMasterMap)
      const normalized = normalizeDateText(text)
      if (toDateObjectFromYmd(normalized)) fallback.push(normalized)
    }
    templateDates = fallback
  }
  const templateMin = templateDates.reduce((min, current) => {
    if (!min) return current
    return dayDiff(current, min) > 0 ? current : min
  }, '')
  const templateMax = templateDates.reduce((max, current) => {
    if (!max) return current
    return dayDiff(max, current) > 0 ? current : max
  }, '')
  const sourceRange = getSourceDateRange(sourceDateColumn)
  const sourceMax = sourceRange.max
  templateDebug.templateMin = templateMin || ''
  templateDebug.templateMax = templateMax || ''
  templateDebug.sourceMin = sourceRange.min || ''
  templateDebug.sourceMax = sourceRange.max || ''
  if (!templateMax || !sourceMax) {
    templateDebug.reason = 'missing-template-or-source-max'
    return range
  }

  const needDays = dayDiff(templateMax, sourceMax)
  templateDebug.needDays = needDays
  if (needDays <= 0) {
    templateDebug.reason = 'needDays<=0'
    return range
  }

  if (axis === 'column') {
    const oldEndRow = range.e.r
    const newEndRow = oldEndRow + needDays
    if (!Array.isArray(sheet['!rows'])) sheet['!rows'] = []
    let rollingDate = templateMax
    for (let r = oldEndRow + 1; r <= newEndRow; r += 1) {
      if (sheet['!rows'][oldEndRow]) {
        sheet['!rows'][r] = { ...sheet['!rows'][oldEndRow] }
      }
      for (let c = range.s.c; c <= range.e.c; c += 1) {
        copyTemplateCellStyle(sheet, oldEndRow, c, r, c)
      }
      // Date column must be written explicitly every row; do not depend on range loop hit.
      copyTemplateCellStyle(sheet, oldEndRow, dateCol, r, dateCol)
      rollingDate = shiftYmd(rollingDate, 1)
      if (rollingDate) setSheetCellValue(sheet, r, dateCol, rollingDate)
    }
    range.e.r = newEndRow
    templateDebug.afterEnd = String(newEndRow + 1)
  } else {
    const oldEndCol = range.e.c
    const newEndCol = oldEndCol + needDays
    if (!Array.isArray(sheet['!cols'])) sheet['!cols'] = []
    let rollingDate = templateMax
    for (let c = oldEndCol + 1; c <= newEndCol; c += 1) {
      if (sheet['!cols'][oldEndCol]) {
        sheet['!cols'][c] = { ...sheet['!cols'][oldEndCol] }
      }
      for (let r = range.s.r; r <= range.e.r; r += 1) {
        copyTemplateCellStyle(sheet, r, oldEndCol, r, c)
      }
      copyTemplateCellStyle(sheet, dateRow, oldEndCol, dateRow, c)
      rollingDate = shiftYmd(rollingDate, 1)
      if (rollingDate) setSheetCellValue(sheet, dateRow, c, rollingDate)
    }
    range.e.c = newEndCol
    templateDebug.afterEnd = colIndexToLabel(newEndCol)
  }

  sheet['!ref'] = XLSX.utils.encode_range(range)
  templateDebug.reason = 'extended'
  if (typeof console !== 'undefined') {
    console.info('[template-extend-debug]', {
      axis: templateDebug.axis,
      sourceDateColumn: templateDebug.sourceDateColumn,
      templateMin: templateDebug.templateMin,
      templateMax: templateDebug.templateMax,
      sourceMin: templateDebug.sourceMin,
      sourceMax: templateDebug.sourceMax,
      needDays: templateDebug.needDays,
      beforeEnd: templateDebug.beforeEnd,
      afterEnd: templateDebug.afterEnd,
      reason: templateDebug.reason
    })
  }
  return range
}

function buildTemplateDateIndexMap({
  sheet,
  mergedCellMasterMap,
  axis,
  startIndex,
  endIndex,
  dateRow,
  dateCol,
  enableContinuousFill
}) {
  const explicitMap = new Map()
  for (let idx = startIndex; idx <= endIndex; idx += 1) {
    const text = axis === 'row'
      ? getSheetCellDateText(sheet, dateRow, idx, mergedCellMasterMap)
      : getSheetCellDateText(sheet, idx, dateCol, mergedCellMasterMap)
    const normalized = normalizeDateText(text)
    if (toDateObjectFromYmd(normalized)) {
      explicitMap.set(idx, normalized)
    }
  }

  if (!enableContinuousFill || !explicitMap.size) return explicitMap

  const result = new Map()
  const explicitIndexes = Array.from(explicitMap.keys()).sort((a, b) => a - b)
  const anchor = explicitIndexes[0]
  const anchorDate = explicitMap.get(anchor)
  if (!anchorDate) return explicitMap

  for (let idx = startIndex; idx <= endIndex; idx += 1) {
    result.set(idx, shiftYmd(anchorDate, idx - anchor))
  }
  explicitMap.forEach((value, idx) => {
    result.set(idx, value)
  })
  return result
}

function updateTemplateMetricOptions() {
  templateMetricOptions.value = []
  if (!templateExport.workbook || !templateExport.sheetName) return
  const sheet = templateExport.workbook.Sheets[templateExport.sheetName]
  if (!sheet || !sheet['!ref']) return
  const range = XLSX.utils.decode_range(sheet['!ref'])
  const mergedCellMasterMap = buildMergedCellMasterMap(sheet)
  const startRow = Math.max(0, Number(templateExport.dataStartRow || 1) - 1)
  const startCol = Math.max(0, Number(templateExport.dataStartCol || 1) - 1)
  const matchRow = Math.max(0, Number(templateExport.matchRow || 1) - 1)
  const matchCol = Math.max(0, Number(templateExport.matchCol || 1) - 1)
  const metricHintRow = Math.max(0, Number(templateExport.metricHintRow || 1) - 1)

  const set = new Set()
  if (templateExport.dateAxis === 'column') {
    for (let c = startCol; c <= range.e.c; c += 1) {
      const matchText = getSheetCellText(sheet, matchRow, c, mergedCellMasterMap)
      const token = splitMatchToken(matchText)
      const hint = getSheetCellText(sheet, metricHintRow, c, mergedCellMasterMap)
      const metricText = String(token.metricText || hint || '').trim()
      if (metricText) set.add(metricText)
    }
  } else {
    for (let r = startRow; r <= range.e.r; r += 1) {
      const matchText = getSheetCellText(sheet, r, matchCol, mergedCellMasterMap)
      const token = splitMatchToken(matchText)
      const metricText = String(token.metricText || '').trim()
      if (metricText) set.add(metricText)
    }
  }

  templateMetricOptions.value = Array.from(set)
  const nextMap = {}
  templateMetricOptions.value.forEach((metricText) => {
    const kept = templateExport.metricBindingMap[metricText]
    if (kept && metricColumnOptions.value.includes(kept)) {
      nextMap[metricText] = kept
      return
    }
    const auto = resolveMetricColumnByName(metricText, metricColumnOptions.value)
    if (auto) nextMap[metricText] = auto
  })
  templateExport.metricBindingMap = nextMap
}

function setSheetCellValue(sheet, row0, col0, value) {
  if (row0 < 0 || col0 < 0) return
  const addr = XLSX.utils.encode_cell({ r: row0, c: col0 })
  const oldCell = sheet[addr] || {}
  const isNum = typeof value === 'number' && Number.isFinite(value)
  sheet[addr] = {
    ...oldCell,
    t: isNum ? 'n' : 's',
    v: isNum ? value : String(value ?? ''),
    w: undefined
  }
}

function colIndexToLabel(col0) {
  let n = col0 + 1
  let label = ''
  while (n > 0) {
    const rem = (n - 1) % 26
    label = String.fromCharCode(65 + rem) + label
    n = Math.floor((n - 1) / 26)
  }
  return label
}

function buildOverrideKey(row0, col0) {
  return `${row0},${col0}`
}

function buildTemplateFillPlan(sheet, mutateSheet = false) {
  const workingSheet = mutateSheet ? sheet : cloneSheetForPreview(sheet)
  const overrides = new Map()
  const ref = workingSheet?.['!ref']
  if (!ref) {
    return { sheet: workingSheet, overrides, scannedCells: 0, candidateCells: 0, matchedCells: 0, unresolvedMetricCells: 0, missingSourceCells: 0 }
  }

  const sourceDateColumn = templateExport.sourceDateColumn || config.headerColumn
  const sourceDimensionColumn = templateExport.matchSourceColumn || config.rowGroupColumn
  const sourceMetricColumns = metricColumnOptions.value
  const valueMap = buildSourceValueMap(sourceDateColumn, sourceDimensionColumn, sourceMetricColumns)
  const mergedCellMasterMap = buildMergedCellMasterMap(workingSheet)
  let range = XLSX.utils.decode_range(ref)
  const startRow = Math.max(0, Number(templateExport.dataStartRow || 1) - 1)
  const startCol = Math.max(0, Number(templateExport.dataStartCol || 1) - 1)
  const dateRow = Math.max(0, Number(templateExport.dateRow || 1) - 1)
  const dateCol = Math.max(0, Number(templateExport.dateCol || 1) - 1)
  const matchRow = Math.max(0, Number(templateExport.matchRow || 1) - 1)
  const matchCol = Math.max(0, Number(templateExport.matchCol || 1) - 1)
  const metricHintRow = Math.max(0, Number(templateExport.metricHintRow || 1) - 1)
  range = extendTemplateRangeIfNeeded(workingSheet, range, {
    axis: templateExport.dateAxis === 'column' ? 'column' : 'row',
    startRow,
    startCol,
    dateRow,
    dateCol,
    sourceDateColumn,
    autoExtend: Boolean(templateExport.autoExtendTemplateDateRange)
  })
  const dateIndexMap = buildTemplateDateIndexMap({
    sheet: workingSheet,
    mergedCellMasterMap,
    axis: templateExport.dateAxis === 'column' ? 'column' : 'row',
    startIndex: templateExport.dateAxis === 'column' ? startRow : startCol,
    endIndex: templateExport.dateAxis === 'column' ? range.e.r : range.e.c,
    dateRow,
    dateCol,
    // Use explicit template dates to avoid day-shift/misalignment during matching.
    enableContinuousFill: false
  })

  let scannedCells = 0
  let candidateCells = 0
  let matchedCells = 0
  let unresolvedMetricCells = 0
  let missingSourceCells = 0

  for (let r = startRow; r <= range.e.r; r += 1) {
    for (let c = startCol; c <= range.e.c; c += 1) {
      scannedCells += 1
      const indexKey = templateExport.dateAxis === 'column' ? r : c
      const dateText = dateIndexMap.get(indexKey)
        || (templateExport.dateAxis === 'column'
          ? getSheetCellDateText(workingSheet, r, dateCol, mergedCellMasterMap)
          : getSheetCellDateText(workingSheet, dateRow, c, mergedCellMasterMap))
      const dateValue = normalizeDateText(dateText)
      if (!dateValue) continue
      const monthDay = extractMonthDay(dateText) || extractMonthDay(dateValue)

      const matchText = templateExport.dateAxis === 'column'
        ? getSheetCellText(workingSheet, matchRow, c, mergedCellMasterMap)
        : getSheetCellText(workingSheet, r, matchCol, mergedCellMasterMap)
      const token = splitMatchToken(matchText)
      const dimension = String(token.dimension || '').trim()
      if (!dimension) continue

      candidateCells += 1
      const metricHintText = getSheetCellText(workingSheet, metricHintRow, c, mergedCellMasterMap)
      const metricColumn = resolveMetricColumnByName(token.metricText || metricHintText, sourceMetricColumns)
      if (!metricColumn) {
        unresolvedMetricCells += 1
        continue
      }

      const valueKeys = [buildDataValueKey(dateValue, dimension, metricColumn)]
      if (monthDay) valueKeys.push(buildDataValueKey(monthDay, dimension, metricColumn))
      const hitKey = valueKeys.find((key) => valueMap.has(key))
      if (hitKey) {
        const sourceValue = valueMap.get(hitKey)
        const numericValue = isNumericLike(sourceValue) ? toNumber(sourceValue) : sourceValue
        overrides.set(buildOverrideKey(r, c), { value: numericValue ?? 0, status: 'matched' })
        matchedCells += 1
      } else {
        overrides.set(buildOverrideKey(r, c), { value: 0, status: 'missing' })
        missingSourceCells += 1
      }
    }
  }

  return { sheet: workingSheet, overrides, scannedCells, candidateCells, matchedCells, unresolvedMetricCells, missingSourceCells, range }
}

function resetTemplatePreview() {
  templatePreviewGrid.value = []
  templatePreviewColHeaders.value = []
  templatePreviewWindow.startRow = 1
  templatePreviewWindow.endRow = 1
  templatePreviewWindow.startColLabel = 'A'
  templatePreviewWindow.endColLabel = 'A'
  templatePreviewStats.scannedCells = 0
  templatePreviewStats.candidateCells = 0
  templatePreviewStats.matchedCells = 0
  templatePreviewStats.unresolvedMetricCells = 0
  templatePreviewStats.missingSourceCells = 0
  templateDebug.axis = ''
  templateDebug.sourceDateColumn = ''
  templateDebug.templateMin = ''
  templateDebug.templateMax = ''
  templateDebug.sourceMin = ''
  templateDebug.sourceMax = ''
  templateDebug.needDays = 0
  templateDebug.beforeEnd = ''
  templateDebug.afterEnd = ''
  templateDebug.reason = ''
}

function buildTemplatePreview() {
  resetTemplatePreview()
  if (!templateExport.visible) return
  if (!templateExport.workbook || !templateExport.sheetName) return
  if (!rawRows.value.length || !templateExport.sourceDateColumn || !templateExport.matchSourceColumn || !metricColumnOptions.value.length) return

  const workbook = templateExport.workbook
  const sheet = workbook.Sheets[templateExport.sheetName]
  if (!sheet || !sheet['!ref']) return

  const plan = buildTemplateFillPlan(sheet, false)
  const previewSheet = plan.sheet || sheet
  const mergedCellMasterMap = buildMergedCellMasterMap(previewSheet)
  const range = plan.range || XLSX.utils.decode_range(previewSheet['!ref'])
  templatePreviewStats.scannedCells = plan.scannedCells || 0
  templatePreviewStats.candidateCells = plan.candidateCells || 0
  templatePreviewStats.matchedCells = plan.matchedCells || 0
  templatePreviewStats.unresolvedMetricCells = plan.unresolvedMetricCells || 0
  templatePreviewStats.missingSourceCells = plan.missingSourceCells || 0

  const startRow = Math.max(0, Number(templateExport.dataStartRow || 1) - 1)
  const startCol = Math.max(0, Number(templateExport.dataStartCol || 1) - 1)
  const headerRow = Math.max(0, Number(templateExport.headerStartRow || 1) - 1)
  const previewStartRow = Math.max(0, Math.min(headerRow, startRow - 1))
  const previewStartCol = Math.max(0, Math.min(startCol - 1, range.s.c))
  const previewMaxRows = Math.max(10, Number(templateExport.previewMaxRows || 32))
  const previewMaxCols = Math.max(8, Number(templateExport.previewMaxCols || 20))
  const previewEndRow = Math.min(range.e.r, previewStartRow + previewMaxRows - 1)
  const previewEndCol = Math.min(range.e.c, previewStartCol + previewMaxCols - 1)

  templatePreviewWindow.startRow = previewStartRow + 1
  templatePreviewWindow.endRow = previewEndRow + 1
  templatePreviewWindow.startColLabel = colIndexToLabel(previewStartCol)
  templatePreviewWindow.endColLabel = colIndexToLabel(previewEndCol)

  const colHeaders = []
  for (let c = previewStartCol; c <= previewEndCol; c += 1) {
    colHeaders.push(colIndexToLabel(c))
  }
  templatePreviewColHeaders.value = colHeaders

  const grid = []
  for (let r = previewStartRow; r <= previewEndRow; r += 1) {
    const cells = []
    for (let c = previewStartCol; c <= previewEndCol; c += 1) {
      const key = buildOverrideKey(r, c)
      const filled = plan.overrides.has(key)
      const override = filled ? plan.overrides.get(key) : null
      const rawValue = filled ? override?.value : getSheetCellText(previewSheet, r, c, mergedCellMasterMap)
      cells.push({
        address: XLSX.utils.encode_cell({ r, c }),
        filled,
        fillStatus: override?.status || '',
        value: String(rawValue ?? '')
      })
    }
    grid.push({
      rowNumber: r + 1,
      cells
    })
  }
  templatePreviewGrid.value = grid
}

function exportByTemplate() {
  if (!templateExport.workbook || !templateExport.sheetName) {
    ElMessage.warning('请先上传模板并选择工作表')
    return
  }
  if (!rawRows.value.length) {
    ElMessage.warning('当前无可导出的原始数据')
    return
  }
  if (!templateExport.sourceDateColumn || !templateExport.matchSourceColumn || !metricColumnOptions.value.length) {
    ElMessage.warning('请先配置源数据日期列、自定义指标匹配原维度和可用指标列')
    return
  }

  const workbook = templateExport.workbook
  const sheet = workbook.Sheets[templateExport.sheetName]
  if (!sheet) {
    ElMessage.error('模板工作表不存在')
    return
  }
  const ref = sheet['!ref']
  if (!ref) {
    ElMessage.error('模板工作表为空')
    return
  }

  const plan = buildTemplateFillPlan(sheet, true)
  let filledCount = 0
  plan.overrides.forEach((entry, key) => {
    const [rText, cText] = key.split(',')
    const r = Number(rText)
    const c = Number(cText)
    setSheetCellValue(sheet, r, c, entry?.value ?? 0)
    filledCount += 1
  })

  const now = new Date()
  const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`
  const baseName = templateExport.fileName
    ? templateExport.fileName.replace(/\.(xlsx|xls)$/i, '')
    : 'template'
  const headerStart = Math.max(1, Number(templateExport.headerStartRow || 1))
  const dataStart = Math.max(headerStart + 1, Number(templateExport.dataStartRow || headerStart + 1))
  const theme = activeExportTheme.value
  const tableHtml = XLSX.utils.sheet_to_html(sheet, { id: 'template-export-table', editable: false })
  const html = `<html><head><meta charset="UTF-8"><style>
#template-export-table{border-collapse:collapse;}
#template-export-table td,#template-export-table th{
  border:1px solid ${theme.border};
  text-align:center;
  vertical-align:middle;
  padding:6px 10px;
  white-space:nowrap;
  color:${theme.text};
}
#template-export-table tr:nth-child(n+${headerStart}):nth-child(-n+${dataStart - 1}) td{
  font-weight:700;
  background:${theme.headerBg};
}
#template-export-table tr:nth-child(n+${dataStart}) td:first-child{
  font-weight:700;
  background:${theme.firstColBg};
}
</style></head><body>${tableHtml}</body></html>`
  downloadExcelHtml(html, `${baseName}_filled_${stamp}.xls`)
  ElMessage.success(`模板导出成功，已填充 ${filledCount} 个单元格`)
  templateExport.visible = false
}

async function copyResultToClipboard() {
  if (!resultAoa.value.length) return
  const text = resultAoa.value.map((row) => row.map((v) => String(v ?? '')).join('\t')).join('\n')
  try {
    await navigator.clipboard.writeText(text)
    ElMessage.success('已复制，可直接粘贴到 Excel')
  } catch (error) {
    console.error(error)
    ElMessage.error('复制失败，请检查浏览器权限')
  }
}

function downloadExcelHtml(html, fileName) {
  const blob = new Blob([`\ufeff${html}`], { type: 'application/vnd.ms-excel;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(link.href)
}

function exportResultExcel() {
  if (!resultAoa.value.length) return
  const theme = activeExportTheme.value
  const styleCell = `border:1px solid ${theme.border};text-align:center;vertical-align:middle;padding:6px 10px;white-space:nowrap;color:${theme.text};`
  const styleHeaderCell = `${styleCell}font-weight:700;background:${theme.headerBg};`
  const styleFirstColBodyCell = `${styleCell}font-weight:700;background:${theme.firstColBg};`
  let html = '<html><head><meta charset="UTF-8"></head><body>'
  html += '<table border="1" cellspacing="0" cellpadding="0" style="border-collapse:collapse;">'

  if (isSecondaryHeaderMode.value && metricColumnsUsed.value.length > 1) {
    const metricCount = metricColumnsUsed.value.length
    html += '<thead>'
    html += '<tr>'
    html += `<th style="${styleHeaderCell}">${escapeHtml(secondHeaderTopLabelUsed.value)}</th>`
    headerValuesUsed.value.forEach((header) => {
      html += `<th style="${styleHeaderCell}" colspan="${metricCount}">${escapeHtml(header)}</th>`
    })
    html += '</tr>'

    html += '<tr>'
    html += `<th style="${styleHeaderCell}">${escapeHtml(secondHeaderBottomLabelUsed.value)}</th>`
    headerValuesUsed.value.forEach(() => {
      metricColumnsUsed.value.forEach((metric) => {
        html += `<th style="${styleHeaderCell}">${escapeHtml(metricDisplayName(metric))}</th>`
      })
    })
    html += '</tr>'
    html += '</thead>'

    html += '<tbody>'
    for (let i = 2; i < resultAoa.value.length; i += 1) {
      const row = resultAoa.value[i] || []
      html += '<tr>'
      row.forEach((cell, colIndex) => {
        const cellStyle = colIndex === 0 ? styleFirstColBodyCell : styleCell
        html += `<td style="${cellStyle}">${escapeHtml(cell)}</td>`
      })
      html += '</tr>'
    }
    html += '</tbody>'
  } else {
    html += '<thead><tr>'
    const headRow = resultAoa.value[0] || []
    headRow.forEach((cell) => {
      html += `<th style="${styleHeaderCell}">${escapeHtml(cell)}</th>`
    })
    html += '</tr></thead>'

    html += '<tbody>'
    for (let i = 1; i < resultAoa.value.length; i += 1) {
      const row = resultAoa.value[i] || []
      html += '<tr>'
      row.forEach((cell, colIndex) => {
        const cellStyle = colIndex === 0 ? styleFirstColBodyCell : styleCell
        html += `<td style="${cellStyle}">${escapeHtml(cell)}</td>`
      })
      html += '</tr>'
    }
    html += '</tbody>'
  }

  html += '</table></body></html>'

  const now = new Date()
  const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`
  downloadExcelHtml(html, `excel_行列提取转换_${stamp}.xls`)
}
</script>

<style scoped>
.excel-pivot-page {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;
  min-width: 0;
  overflow-x: hidden;
}

.excel-pivot-page > * {
  min-width: 0;
}

.panel-card {
  border-radius: 12px;
  min-width: 0;
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.title-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
}

.guide-btn-anchor {
  display: inline-flex;
  align-items: center;
}

.panel-tip {
  font-size: 12px;
  color: #6b7280;
  word-break: break-word;
}

.input-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 12px 0;
  flex-wrap: wrap;
}

.config-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(280px, 1fr));
  gap: 12px;
}

.config-item {
  display: grid;
  gap: 6px;
  min-width: 0;
}

.config-item .label {
  font-size: 13px;
  color: #374151;
}

.preview-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.theme-select {
  width: 170px;
}

.table-scroll-wrap {
  display: block;
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

.excel-pivot-page :deep(.el-card__body) {
  min-width: 0;
  overflow-x: hidden;
}

.table-scroll-wrap :deep(.el-table__inner-wrapper) {
  overflow-x: auto;
}

.table-scroll-wrap :deep(.el-table) {
  width: 100%;
}

.config-item :deep(.el-select),
.config-item :deep(.el-input),
.config-item :deep(.el-textarea),
.config-item :deep(.el-input-number) {
  width: 100%;
  max-width: 100%;
}

.template-metric-map {
  grid-column: 1 / -1;
}

.metric-binding-list {
  display: grid;
  gap: 8px;
}

.metric-binding-row {
  display: grid;
  grid-template-columns: minmax(180px, 280px) minmax(220px, 1fr);
  align-items: center;
  gap: 8px;
}

.metric-binding-left {
  font-size: 12px;
  color: #374151;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  padding: 6px 8px;
  border-radius: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.template-preview {
  margin-top: 12px;
  display: grid;
  gap: 8px;
}

.template-preview-matrix th,
.template-preview-matrix td {
  font-size: 12px;
}

.template-preview-matrix .template-row-header {
  background: #f8fafc;
  font-weight: 600;
  position: sticky;
  left: 0;
  z-index: 2;
}

.template-preview-matrix .template-filled-cell {
  background: #eefbf4;
}

.template-preview-matrix .template-missing-cell {
  background: #fff7e6;
}

.preview-table-box {
  min-width: max-content;
}

.preview-matrix {
  border-collapse: collapse;
  width: max-content;
  min-width: 100%;
  font-size: 13px;
}

.preview-matrix th,
.preview-matrix td {
  border: 1px solid var(--preview-border-color, #8FA3BF);
  padding: 8px 10px;
  text-align: center;
  white-space: nowrap;
  background: #fff;
  color: var(--preview-text-color, #1F2D3D);
}

.preview-matrix thead th {
  background: var(--preview-header-bg, #DCEBFF);
  font-weight: 600;
}

.preview-matrix .first-col {
  position: sticky;
  left: 0;
  z-index: 2;
  background: var(--preview-first-col-bg, #E8F1FB);
  min-width: 180px;
  font-weight: 600;
}

.preview-matrix tbody .first-col {
  background: var(--preview-first-col-body-bg, #E8F1FB);
  z-index: 1;
}

@media (max-width: 960px) {
  .config-grid {
    grid-template-columns: 1fr;
  }

  .metric-binding-row {
    grid-template-columns: 1fr;
  }
}
</style>
